/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: AiProvidersFormView.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    AI Providers creation and editing form.
────────────────────────────────────────────────────────────────*/

import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useCallback } from 'react';
import { Bot, Search, Copy } from 'lucide-react';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { FormBox } from '../../components/ui/FormBox';
import { ModuleFormHeader } from '../../components/layouts';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { useI18n } from '../../hooks/useI18n';
import { useFixedActionBar } from '../../hooks/useFixedActionBar';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { useNotifications } from '../../contexts/NotificationContext';
import { navigateToAiProviderDuplicate } from './aiProvidersActions';
import { useProviderData } from './hooks/useProviderData';
import { useProviderForm } from './hooks/useProviderForm';
import { useProviderSubmit } from './hooks/useProviderSubmit';
import { useQuickList } from './hooks/useQuickList';
import type { AiProviderForm } from './hooks/useProviderData';

type QuickListProvider = {
  id?: number;
  name?: string;
  provider?: string;
  base_url?: string | null;
};
import { useProviderActions } from './hooks/useProviderActions';
import { ProviderFormFields } from './components/ProviderFormFields';

export default function AiProvidersFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showInfo } = useNotifications();

  const duplicateId = searchParams.get('duplicate');
  const resetParam = searchParams.get('_reset');
  const isEditMode = Boolean(id && id !== 'new' && !duplicateId);
  const mode = isEditMode ? 'edit' : 'create';

  // Hook to handle fixed action bar
  useFixedActionBar(true);

  // Data hooks
  const {
    formData,
    setFormData,
    baseline,
    setBaseline,
    isLoading
  } = useProviderData(id, duplicateId, resetParam);

  // Form hooks
  const {
    errors,
    hasChanges,
    validateForm,
    handleInputChange,
    inputClasses
  } = useProviderForm(formData, baseline, mode, setFormData);

  // Submit hooks
  const {
    isSaving,
    isDeleting,
    showDeleteModal,
    setShowDeleteModal,
    submitProvider,
    handleCancel,
    handleDelete
  } = useProviderSubmit(
    formData,
    baseline,
    hasChanges,
    validateForm,
    mode,
    id,
    setFormData,
    setBaseline
  );

  // Create internal save handler that wraps submitProvider
  const handleSaveInternal = useCallback(async (): Promise<AiProviderForm | undefined> => {
    return await submitProvider();
  }, [submitProvider]);

  // Integrate useSaveWithAction
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/admin/ai-integration/providers',
    newPath: '/admin/ai-integration/providers/new',
    mode,
    getEditPath: (savedData: AiProviderForm) => {
      if (savedData.id) {
        return `/admin/ai-integration/providers/${savedData.id}`;
      }
      return '/admin/ai-integration/providers';
    }
  });

  // Quick list hooks
  const {
    showQuickList,
    setShowQuickList,
    quickListSearch,
    setQuickListSearch,
    filteredQuickList,
    isQuickListLoading,
    handleItemSelect,
    currentItemId
  } = useQuickList(mode, id, duplicateId);

  // Actions hooks
  const {
    testingConnection,
    refreshingCapabilities,
    refreshingVoices,
    handleTestConnection,
    handleRefreshCapabilities,
    handleRefreshVoices
  } = useProviderActions(formData, mode, id);

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <ModuleFormHeader
        icon={<Bot className="h-5 w-5 text-primary" />}
        title={isEditMode ? `${t('aiProviders.actions.edit')} - ${formData.name || ''}` : t('aiProviders.actions.create')}
        backButtonText={t('aiProviders.actions.list')}
        onBack={() => navigate('/admin/ai-integration/providers')}
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('aiProviders.quickListButton'),
            onClick: () => setShowQuickList(true)
          },
          ...(isEditMode && id && id !== 'new' && !duplicateId
            ? [
              {
                icon: <Copy className="h-5 w-5 text-muted-foreground" />,
                tooltip: t('common.duplicate'),
                onClick: () => navigateToAiProviderDuplicate(navigate, id)
              }
            ]
            : [])
        ]}
      />

      <form className="mt-8 space-y-4" onSubmit={event => event.preventDefault()}>
        <FormBox title={t('aiProviders.sections.providerConfig')} icon={<Bot />}>
          <ProviderFormFields
            formData={formData}
            errors={errors}
            isEditMode={isEditMode}
            testingConnection={testingConnection}
            refreshingCapabilities={refreshingCapabilities}
            refreshingVoices={refreshingVoices}
            onInputChange={handleInputChange}
            inputClasses={inputClasses}
            onTestConnection={handleTestConnection}
            onRefreshCapabilities={handleRefreshCapabilities}
            onRefreshVoices={handleRefreshVoices}
          />
        </FormBox>
      </form>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={isEditMode ? () => setShowDeleteModal(true) : undefined}
        isEditMode={isEditMode}
        saveText={isSaving ? t('common.loading') : t('common.save')}
        cancelText={t('aiProviders.actions.cancel')}
        deleteText={t('common.delete')}
        saveDisabled={isSaving}
        isLoading={isSaving}
        hasChanges={hasChanges}
        useSaveDropdown={true}
        showSave={true}
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
        }}
        title={t('aiProviders.quickListTitle', 'Quick List - Select Provider')}
        items={filteredQuickList as any}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(item) => handleItemSelect(item as QuickListProvider)}
        currentItemId={currentItemId}
        renderItem={(provider, isCurrent) => (
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-foreground">{provider.name ?? t('common.unknown')}</p>
              {provider.provider && (
                <p className="text-sm text-muted-foreground">{provider.provider}</p>
              )}
              {provider.base_url && (
                <p className="text-xs text-muted-foreground">{provider.base_url}</p>
              )}
            </div>
            {isCurrent && (
              <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded">
                {t('aiProviders.current', 'Current')}
              </span>
            )}
          </div>
        )}
        searchPlaceholder={t('aiProviders.quickListSearchPlaceholder', 'Search by name, provider or base URL')}
        emptyMessage={t('aiProviders.quickListPrompt', 'Start typing to search...')}
        noResultsMessage={t('aiProviders.quickListEmpty', 'No providers found')}
        headerIcon={<Bot className="h-5 w-5" />}
        minSearchChars={0}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={async () => {
          await handleDelete();
          navigate('/admin/ai-integration/providers');
        }}
        title={t('aiProviders.deleteConfirmTitle')}
        message={t('aiProviders.deleteConfirmMessage')}
        confirmText={t('aiProviders.deleteConfirm')}
        cancelText={t('common.cancel')}
        isLoading={isDeleting}
        type="danger"
      />
    </div>
  );
}
